import React, { useState } from "react";
import { MapPin, Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const iconFor = (code, size = 20) => {
  const props = { size, strokeWidth: 1.6, "aria-hidden": true };
  if (code <= 1) return <Sun {...props} style={{ color: "#F5B301" }} />;
  if (code === 2) return <CloudSun {...props} style={{ color: "#F5B301" }} />;
  if (code === 3) return <Cloud {...props} style={{ color: "#8a97a8" }} />;
  if (code <= 48) return <CloudFog {...props} style={{ color: "#8a97a8" }} />;
  if (code <= 57) return <CloudDrizzle {...props} style={{ color: "#5c86c5" }} />;
  if (code <= 67 || (code >= 80 && code <= 82)) return <CloudRain {...props} style={{ color: "#5c86c5" }} />;
  if (code <= 77 || code === 85 || code === 86) return <CloudSnow {...props} style={{ color: "#7fa6d9" }} />;
  return <CloudLightning {...props} style={{ color: "#6b7a99" }} />;
};

const Shell = ({ children }) => (
  <section className="rounded-[10px] border border-border p-4 h-full flex flex-col"
           style={{ background: "linear-gradient(135deg, #EAF4FD 0%, #FDFBE8 100%)" }}
           data-testid="weather-card">
    {children}
  </section>
);

export const WeatherCard = ({ weather, isAdmin, onCitySaved }) => {
  const [city, setCity] = useState("");
  const [saving, setSaving] = useState(false);

  if (!weather?.available) {
    const noCity = weather?.reason === "no_city";
    // Fornitore giù (e nessuna cache utilizzabile): il riquadro SPARISCE in
    // silenzio. Un riquadro che dichiara di non funzionare in prima pagina
    // costa più di dieci errori nascosti.
    if (!noCity || !isAdmin) return null;
    const saveCity = async () => {
      const c = city.trim();
      if (!c) return;
      setSaving(true);
      try {
        await api.put("/org/profile", { city: c });
        toast.success("Città salvata");
        onCitySaved?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
      } finally {
        setSaving(false);
      }
    };
    return (
      <Shell>
        <div className="flex-1 flex flex-col justify-center gap-2" data-testid="weather-unavailable">
          <div className="text-sm font-medium" style={{ color: "var(--wl-ink)" }}>Previsioni meteo</div>
          <div className="text-xs" style={{ color: "var(--wl-text-secondary)" }}>
            Imposta la città della tua azienda per vedere le previsioni.
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            <input value={city} onChange={(e) => setCity(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") saveCity(); }}
                   placeholder="Es. Sesto Fiorentino"
                   className="flex-1 min-w-0 text-sm px-2.5 py-1.5 rounded-lg border border-border bg-white/70 focus:outline-none focus:border-neutral-400"
                   data-testid="weather-city-input" />
            <button type="button" onClick={saveCity} disabled={saving || !city.trim()}
                    className="wl-btn wl-btn--primary wl-btn--sm shrink-0" data-testid="weather-city-save">
              {saving ? <Loader2 size={13} className="animate-spin" /> : "Salva"}
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-1.5 text-sm font-medium mb-1" style={{ color: "var(--wl-ink)" }}>
        <MapPin size={13} strokeWidth={1.8} aria-hidden="true" />
        <span data-testid="weather-city">{weather.city}</span>
      </div>
      <div className="flex items-start justify-between">
        <div className="text-4xl font-semibold leading-none"
             style={{ fontFamily: "var(--wl-font-display)", color: "var(--wl-ink)" }}
             data-testid="weather-temp">
          {weather.temp}°
        </div>
        <div className="mt-0.5">{iconFor(weather.code, 34)}</div>
      </div>
      <div className="text-xs mt-2" style={{ color: "var(--wl-text-secondary)" }} data-testid="weather-condition">
        {weather.condition} · max {weather.tmax}° min {weather.tmin}° · vento {weather.wind_kmh} km/h
      </div>
      {weather.phrase && (
        <div className="text-xs mt-2 px-2.5 py-1.5 rounded-lg bg-white/60 border border-white/80"
             style={{ color: "var(--wl-ink-soft)" }} data-testid="weather-phrase">
          {weather.phrase}
        </div>
      )}
      {(weather.hours || []).length > 0 && (
        <div className="flex items-end justify-between gap-1 mt-auto pt-3" data-testid="weather-hours">
          {weather.hours.map((h) => (
            <div key={h.time} className="text-center">
              <div className="text-[10px]" style={{ color: "var(--wl-text-secondary)" }}>{h.time}</div>
              <div className="flex justify-center my-0.5">{iconFor(h.code, 13)}</div>
              <div className="text-[11px] font-semibold" style={{ color: "var(--wl-ink)" }}>{h.temp}°</div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
};
